/**
 * Catalog registry — maps every supported locale to its message catalog, loading
 * only what the page needs.
 *
 * `fr` is the source (and the type origin); `en` and `ar` are declared against
 * `TranslationKeys`, so every catalog is complete and structurally parallel at
 * compile time.
 *
 * Bundle strategy: the DEFAULT locale (English — what a first visit without a
 * French/Arabic browser gets) is imported statically and ships in the main
 * bundle; every other locale is a dynamic `import()` → its own chunk, fetched
 * only when that locale is active (`loadLocale`). The app bootstrap awaits the
 * active locale's catalog BEFORE importing the application modules, so module-
 * level `tr()` copy and the very first render are already in the right language.
 *
 * Resolution (see useT.translate): active catalog → French source (when loaded)
 * → English (always loaded) → the key itself.
 */

import { DEFAULT_LOCALE, type Locale } from '../config';

import en from './en';
import type { TranslationKeys } from './fr';

/** Dynamic importers for the lazily-loaded locales (one chunk each). */
const LOADERS: Partial<Record<Locale, () => Promise<{ default: TranslationKeys }>>> = {
  fr: () => import('./fr'),
  ar: () => import('./ar'),
};

/**
 * Loaded catalogs. Mutable on purpose (filled by `loadLocale`); read through
 * `catalogs[locale]`, which is `undefined` until that locale has been loaded.
 */
export const catalogs: Partial<Record<Locale, TranslationKeys>> = { [DEFAULT_LOCALE]: en };

const inflight = new Map<Locale, Promise<TranslationKeys>>();

/** True once `locale`'s catalog is in memory (the default locale always is). */
export function isLocaleLoaded(locale: Locale): boolean {
  return catalogs[locale] !== undefined;
}

/**
 * Fetch (once) and register a locale's catalog. Concurrent calls share the same
 * request; a failed fetch is not cached, so a retry can succeed.
 */
export function loadLocale(locale: Locale): Promise<TranslationKeys> {
  const loaded = catalogs[locale];
  if (loaded) return Promise.resolve(loaded);
  const pending = inflight.get(locale);
  if (pending) return pending;
  const loader = LOADERS[locale];
  if (!loader) return Promise.reject(new Error(`No catalog loader for locale "${locale}"`));
  const p = loader()
    .then((mod) => {
      catalogs[locale] = mod.default;
      return mod.default;
    })
    .finally(() => inflight.delete(locale));
  inflight.set(locale, p);
  return p;
}

export type { TranslationKeys };
